import React from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, AlertCircle } from 'lucide-react';

interface TimelineEvent {
  label: string;
  value: string | number;
  status: 'completed' | 'in-progress' | 'pending';
  timestamp?: string;
  color?: string;
}

interface TimelineChartProps {
  title: string;
  events: TimelineEvent[];
}

const TimelineChart: React.FC<TimelineChartProps> = ({ title, events }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check size={16} />;
      case 'in-progress':
        return <Clock size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'in-progress':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  return (
    <motion.div
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-6">{title}</h3>

      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-6">
          {events.map((event, index) => {
            const color = event.color || getStatusColor(event.status);

            return (
              <motion.div
                key={index}
                className="relative flex items-start gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Icon */}
                <motion.div
                  className="relative z-10 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: color }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1 + 0.2, type: 'spring', stiffness: 200 }}
                  whileHover={{ scale: 1.1 }}
                >
                  <div className="text-white">
                    {getStatusIcon(event.status)}
                  </div>

                  {/* Pulse Effect for in-progress */}
                  {event.status === 'in-progress' && (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ backgroundColor: color }}
                      animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.div>

                {/* Content */}
                <motion.div
                  className="flex-1 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{event.label}</h4>
                    {event.timestamp && (
                      <span className="text-xs text-gray-500">{event.timestamp}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <motion.div
                      className="text-2xl font-bold"
                      style={{ color }}
                      initial={{ scale: 0.5 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1 + 0.4, type: 'spring' }}
                    >
                      {event.value}
                    </motion.div>
                    
                    {/* Status Badge */}
                    <span
                      className="px-2 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: color }}
                    >
                      {event.status}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default TimelineChart;

